/**
 * Import mapping configuration — the persisted, shareable shape stored in
 * `import_mappings.config` and round-tripped through the import wizard.
 *
 * Pure types only (no runtime deps) so the core transform/group/validate
 * modules and their tests can import them freely.
 */

/**
 * How rows in the file relate to records + form instances:
 * - `single`           — single-instance form; one row → one record's one submission.
 * - `repeatable_long`  — repeatable form; rows sharing the grouping key form one
 *                        record, each row is an instance (e.g. one CBC per row).
 * - `repeatable_wide`  — repeatable form; one row is one record, instances are
 *                        repeated column blocks (cbc_1_*, cbc_2_* …).
 */
export type ImportShape = 'single' | 'repeatable_long' | 'repeatable_wide'

/** How an imported record is reconciled with the database. */
export type RecordIdentityMode = 'create_new' | 'match_display_id' | 'match_field'

export type CommitStatus = 'draft' | 'submitted'

export type TransformKind = 'text' | 'number' | 'boolean' | 'date' | 'select'

export interface ColumnTransform {
  kind: TransformKind
  /** date: source calendar of the raw string. */
  sourceCalendar?: 'jalali' | 'gregorian'
  /** date: source format token, e.g. 'YYYY/MM/DD' or 'YYYY-MM-DD HH:mm'. */
  sourceFormat?: string
  /** date: whether the field is a datetime (keeps the time half). */
  isDateTime?: boolean
  /** boolean: raw strings (lower-cased) that mean `true`. */
  trueValues?: string[]
  /** select / multi_select: raw value → option_key. */
  valueMap?: Record<string, string>
  /** multi_select: delimiter used to split a single cell into many values. */
  multiDelimiter?: string
}

export interface ColumnMapping {
  /** CSV/XLSX header. */
  column: string
  /** Target field_key. Empty string = column ignored. */
  fieldKey: string
  transform: ColumnTransform
}

/** A repeated column block for `repeatable_wide` — one form instance per block. */
export interface WideInstanceBlock {
  instanceLabel: string
  columns: ColumnMapping[]
}

export interface RecordIdentity {
  mode: RecordIdentityMode
  /** create_new + long: columns whose equal values group rows into one record.
   *  Empty ⇒ one record per row. */
  groupByColumns: string[]
  /** match_display_id: column holding the record display_id. */
  displayIdColumn?: string
  /** match_field: the form field_key to match on … */
  matchFieldKey?: string
  /** … and the column carrying its value. */
  matchColumn?: string
}

export interface MappingConfig {
  shape: ImportShape
  identity: RecordIdentity
  /** single / repeatable_long: flat column→field mappings. */
  columns: ColumnMapping[]
  /** repeatable_wide: per-instance column blocks. */
  wideBlocks: WideInstanceBlock[]
}

export function emptyMappingConfig(): MappingConfig {
  return {
    shape: 'single',
    identity: { mode: 'create_new', groupByColumns: [] },
    columns: [],
    wideBlocks: [],
  }
}

export function defaultTransformFor(fieldType: string): ColumnTransform {
  switch (fieldType) {
    case 'number':
    case 'calculated':
      return { kind: 'number' }
    case 'boolean':
      return { kind: 'boolean', trueValues: ['true', '1', 'yes', 'بله', 'بلی', 'دارد'] }
    case 'date':
      return { kind: 'date', sourceCalendar: 'jalali', sourceFormat: 'YYYY/MM/DD', isDateTime: false }
    case 'datetime':
      return { kind: 'date', sourceCalendar: 'jalali', sourceFormat: 'YYYY/MM/DD HH:mm', isDateTime: true }
    case 'time':
      return { kind: 'text' }
    case 'single_select':
      return { kind: 'select', valueMap: {} }
    case 'multi_select':
      return { kind: 'select', valueMap: {}, multiDelimiter: ',' }
    default:
      return { kind: 'text' }
  }
}
